// PostgREST query builders are typed at the shared client boundary.
// Canonical identity retains both Google list and item IDs. No missing-item diff
// is performed: only explicit provider records can change or delete a copy.
const PREFIX = "google_tasks:v1:";
const ROW_FIELDS = "id,user_id,title,priority,due_date,time_label,done,created_at,source,external_id,client_action_id,completed_at";
const PAGE_SIZE = 500;
const MAX_LOCAL_PAGES = 200;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const validId = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length <= 1000 && !/[\u0000-\u001f\u007f]/.test(value);

export interface GoogleTaskIdentity { listId: string; itemId: string }
export interface GoogleTaskRecord {
  id: string;
  title?: string;
  status?: string;
  due?: string;
  completed?: string;
  deleted?: boolean;
  hidden?: boolean;
}
export interface GoogleTaskLocalRow {
  id: string;
  user_id: string;
  title: string;
  priority: string;
  due_date: string | null;
  time_label: string | null;
  done: boolean;
  created_at: string | null;
  source: string;
  external_id: string | null;
  client_action_id: string | null;
  completed_at: string | null;
}
export interface GoogleTaskSyncResult {
  status: "ok" | "conflict" | "failed" | "skipped";
  change: "inserted" | "updated" | "deleted" | "unchanged" | null;
  task_id?: string;
  row?: GoogleTaskLocalRow | null;
  error?: string;
}

export function encodeGoogleTaskExternalId(listId: string, itemId: string): string {
  if (!validId(listId) || !validId(itemId)) throw new Error("google_tasks_invalid_identity");
  const bytes = new TextEncoder().encode(JSON.stringify([listId, itemId]));
  return PREFIX + btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeGoogleTaskExternalId(value: unknown): GoogleTaskIdentity | null {
  if (typeof value !== "string" || !value.startsWith(PREFIX) || value.length > 11000) return null;
  try {
    const encoded = value.slice(PREFIX.length);
    if (!/^[A-Za-z0-9_-]+$/.test(encoded)) return null;
    const binary = atob(encoded.replace(/-/g, "+").replace(/_/g, "/"));
    const parsed = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(Uint8Array.from(binary, c => c.charCodeAt(0))));
    if (!Array.isArray(parsed) || parsed.length !== 2 || !validId(parsed[0]) || !validId(parsed[1])) return null;
    if (encodeGoogleTaskExternalId(parsed[0], parsed[1]) !== value) return null;
    return { listId: parsed[0], itemId: parsed[1] };
  } catch { return null; }
}

export async function loadGoogleTaskRows(sb: any, userId: string): Promise<GoogleTaskLocalRow[]> {
  const rows: GoogleTaskLocalRow[] = [];
  let cursor = "";
  for (let page = 0; page < MAX_LOCAL_PAGES; page++) {
    let query = sb.from("tasks").select(ROW_FIELDS).eq("user_id", userId).eq("source", "google_tasks").order("id", { ascending: true }).limit(PAGE_SIZE);
    if (cursor) query = query.gt("id", cursor);
    const result = await query;
    if (result.error || !Array.isArray(result.data)) throw new Error("google_tasks_local_read_failed");
    const incoming = result.data as GoogleTaskLocalRow[];
    if (!incoming.length) return rows;
    let previous = cursor;
    for (const row of incoming) {
      if (!checkedRow(row, userId, row.external_id)) throw new Error("google_tasks_local_identity_mismatch");
      if (previous && row.id <= previous) throw new Error("google_tasks_local_cursor_invalid");
      previous = row.id;
    }
    rows.push(...incoming);
    // A project's max_rows can be lower than the requested page size. Only an
    // empty keyset page proves exhaustion; a short nonempty page does not.
    const next = incoming[incoming.length - 1]?.id;
    if (!next || (cursor && next <= cursor)) throw new Error("google_tasks_local_cursor_invalid");
    cursor = next;
  }
  throw new Error("google_tasks_local_coverage_limit");
}

// Executor captures this before contacting Google. A remote success followed
// by a local conflict is a confirmed provider result, never an uncertain write.
export async function loadGoogleTaskSnapshot(sb: any, userId: string, listId: string, itemId: string): Promise<GoogleTaskLocalRow[]> {
  const externalId = encodeGoogleTaskExternalId(listId, itemId);
  const result = await sb.from("tasks").select(ROW_FIELDS).eq("user_id", userId).eq("source", "google_tasks")
    .in("external_id", [externalId, itemId]).limit(3);
  if (result.error || !Array.isArray(result.data)) throw new Error("google_tasks_local_read_failed");
  if (result.data.length > 2 || result.data.some((row: GoogleTaskLocalRow) => ![externalId, itemId].includes(row.external_id || "") || !checkedRow(row, userId, row.external_id))) throw new Error("google_tasks_local_identity_mismatch");
  return result.data as GoogleTaskLocalRow[];
}

function timestamp(value: unknown): string | null | undefined {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/.test(value) || !Number.isFinite(Date.parse(value))) return undefined;
  const day = value.slice(0, 10);
  if (new Date(day + "T00:00:00Z").toISOString().slice(0, 10) !== day) return undefined;
  return new Date(value).toISOString();
}

function checkedRow(value: unknown, userId: string, externalId: string | null): GoogleTaskLocalRow | null {
  if (!value || typeof value !== "object") return null;
  const row = value as GoogleTaskLocalRow;
  if (typeof row.id !== "string" || !UUID_RE.test(row.id) ||
    row.user_id !== userId || row.source !== "google_tasks" || row.external_id !== externalId || (row.external_id !== null && typeof row.external_id !== "string") || typeof row.title !== "string" ||
    !["Critical", "Important", "Normal"].includes(row.priority) || typeof row.done !== "boolean" ||
    [row.due_date, row.created_at, row.completed_at].some(value => value !== null && (typeof value !== "string" || !Number.isFinite(Date.parse(value)))) ||
    [row.time_label, row.client_action_id].some(value => value !== null && typeof value !== "string")) return null;
  return row;
}

function compareRow(query: any, row: GoogleTaskLocalRow) {
  query = query.eq("id", row.id).eq("user_id", row.user_id).eq("source", "google_tasks");
  // Priority and time_label are local-only and omitted from the patch. Their
  // concurrent edits can safely survive an unrelated provider update.
  for (const key of ["external_id", "title", "due_date", "done", "completed_at"] as const) {
    query = row[key] === null ? query.is(key, null) : query.eq(key, row[key]);
  }
  return query;
}

export async function reconcileGoogleTask(options: {
  sb: any; userId: string; listId: string; task: GoogleTaskRecord;
  existingRows: GoogleTaskLocalRow[]; legacyIdentityVerified?: boolean;
}): Promise<GoogleTaskSyncResult> {
  const { sb, userId, listId, task, existingRows, legacyIdentityVerified = false } = options;
  try {
    if (!task || !validId(task.id) || !validId(listId)) return { status: "skipped", change: null, error: "invalid_provider_identity" };
    const externalId = encodeGoogleTaskExternalId(listId, task.id);
    if (!Array.isArray(existingRows) || existingRows.some(row => !checkedRow(row, userId, row.external_id))) return { status: "failed", change: null, error: "invalid_local_snapshot" };
    const ownerRows = existingRows;
    const canonical = ownerRows.filter(row => row.external_id === externalId);
    const legacy = ownerRows.filter(row => row.external_id === task.id);
    if (canonical.length > 1 || legacy.length > 1 || (canonical.length && legacy.length) || (legacy.length && !legacyIdentityVerified)) {
      return { status: "conflict", change: null, error: "ambiguous_legacy_identity" };
    }
    const existing = canonical[0] || legacy[0];
    if (task.deleted === true) {
      // Deleted tombstones usually omit content. Uniqueness proves identity,
      // but cannot prove a legacy copy has no earlier local edits to preserve.
      if (legacy.length) return { status: "conflict", change: null, error: "legacy_delete_unresolved" };
      if (!existing) return { status: "ok", change: "unchanged" };
      const result = await compareRow(sb.from("tasks").delete(), existing).select("id");
      if (result.error) return { status: "failed", change: null, error: "local_delete_failed" };
      if (!result.data?.length) return { status: "conflict", change: null, error: "local_copy_changed" };
      if (result.data.length !== 1 || result.data[0].id !== existing.id) return { status: "failed", change: null, error: "local_readback_failed" };
      return { status: "ok", change: "deleted", task_id: existing.id, row: null };
    }
    const title = typeof task.title === "string" ? task.title.trim() : "";
    if (!title || title.length > 1024 || !["needsAction", "completed"].includes(task.status || "")) return { status: "skipped", change: null, error: "invalid_provider_task" };
    const due = timestamp(task.due);
    // Google can omit a completion timestamp. That is unknown metadata, not
    // evidence that a completion date already stored in VYV should be erased.
    const completed = task.status === "completed"
      ? (task.completed === undefined ? timestamp(existing?.completed_at) : timestamp(task.completed)) : null;
    if (due === undefined || completed === undefined) return { status: "skipped", change: null, error: "invalid_provider_date" };
    // Tasks API has date-only due semantics: never assign a provider time_label.
    const dueDate = due ? String(task.due).slice(0, 10) + "T00:00:00.000Z" : null;
    const patch = { title, due_date: dueDate, done: task.status === "completed", completed_at: completed, external_id: externalId };
    if (existing) {
      const contentMatches = existing.title === title && timestamp(existing.due_date) === dueDate && existing.done === patch.done && timestamp(existing.completed_at) === completed;
      // Before canonical identity existed VYV allowed edits to local imported
      // copies. There is no edit/version baseline that can attribute a prior
      // divergence to Google or VYV. Keep it for review rather than overwrite.
      if (legacy.length && !contentMatches) return { status: "conflict", change: null, error: "legacy_content_diverged" };
      if (contentMatches && existing.external_id === externalId) {
        const result = await compareRow(sb.from("tasks").select(ROW_FIELDS), existing);
        if (result.error) return { status: "failed", change: null, error: "local_readback_failed" };
        const row = checkedRow(result.data?.[0], userId, externalId);
        if (!row || result.data.length !== 1 || row.id !== existing.id || row.title !== patch.title || row.done !== patch.done || timestamp(row.due_date) !== patch.due_date || timestamp(row.completed_at) !== patch.completed_at) return { status: "conflict", change: null, error: "local_copy_changed" };
        return { status: "ok", change: "unchanged", task_id: existing.id, row };
      }
      const result = await compareRow(sb.from("tasks").update(patch), existing).select(ROW_FIELDS);
      if (result.error) return { status: "failed", change: null, error: "local_update_failed" };
      if (!result.data?.length) return { status: "conflict", change: null, error: "local_copy_changed" };
      const row = checkedRow(result.data[0], userId, externalId);
      if (!row || result.data.length !== 1 || row.id !== existing.id || row.title !== patch.title || row.done !== patch.done || timestamp(row.due_date) !== patch.due_date || timestamp(row.completed_at) !== patch.completed_at) return { status: "failed", change: null, error: "local_readback_failed" };
      return { status: "ok", change: "updated", task_id: existing.id, row };
    }
    const result = await sb.from("tasks").upsert({ ...patch, user_id: userId, source: "google_tasks", priority: "Normal" },
      { onConflict: "user_id,source,external_id", ignoreDuplicates: true }).select(ROW_FIELDS);
    if (result.error) return { status: result.error.code === "23505" ? "conflict" : "failed", change: null, error: "local_insert_failed" };
    if (!result.data?.length) return { status: "conflict", change: null, error: "local_copy_changed" };
    const row = checkedRow(result.data[0], userId, externalId);
    if (!row || result.data.length !== 1 || row.title !== patch.title || row.done !== patch.done || timestamp(row.due_date) !== patch.due_date || timestamp(row.completed_at) !== patch.completed_at) return { status: "failed", change: null, error: "local_readback_failed" };
    return { status: "ok", change: "inserted", task_id: row.id, row };
  } catch (cause) {
    console.error("[google-task-sync] local reconciliation failed", cause instanceof Error ? cause.message : "unknown");
    return { status: "failed", change: null, error: "local_reconcile_failed" };
  }
}
